import React, { useState, useEffect } from 'react';
import { X, Building2, Search, Check } from 'lucide-react';
import { teamAPI, publicationAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

interface TeamAffiliationModalProps {
  isOpen: boolean;
  onClose: () => void;
  publicationId: number;
  publicationTitle: string;
  onSuccess?: () => void;
}

interface Team {
  id: number;
  full_name: string;
  institution?: string;
  lab_name?: string;
}

const TeamAffiliationModal: React.FC<TeamAffiliationModalProps> = ({
  isOpen,
  onClose,
  publicationId,
  publicationTitle,
  onSuccess
}) => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadTeams();
      loadPublicationTeams();
    }
  }, [isOpen, publicationId]);

  const loadTeams = async () => {
    try {
      setIsLoadingTeams(true);
      const allTeams = await teamAPI.getTeams();
      setTeams(allTeams);
    } catch (error) {
      console.error('Failed to load teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setIsLoadingTeams(false);
    }
  };

  const loadPublicationTeams = async () => {
    try {
      setIsLoading(true);
      const publicationTeams = await publicationAPI.getPublicationTeams(publicationId);
      setSelectedTeamIds(publicationTeams.map((team: Team) => team.id));
    } catch (error) {
      console.error('Failed to load publication teams:', error);
      // If endpoint doesn't exist yet, just continue with empty selection
      setSelectedTeamIds([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTeams = teams.filter(team => {
    const fullName = team.full_name || '';
    const institution = team.institution || '';
    const labName = team.lab_name || '';
    const searchLower = searchTerm.toLowerCase();
    
    return fullName.toLowerCase().includes(searchLower) ||
           institution.toLowerCase().includes(searchLower) ||
           labName.toLowerCase().includes(searchLower);
  });

  const handleTeamToggle = (teamId: number) => {
    setSelectedTeamIds(prev => {
      if (prev.includes(teamId)) {
        return prev.filter(id => id !== teamId);
      } else {
        return [...prev, teamId];
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      setIsSubmitting(true);
      await publicationAPI.setPublicationTeams(publicationId, { team_ids: selectedTeamIds });
      toast.success('Team affiliations updated successfully');
      handleClose();
      // Call onSuccess after closing modal to ensure navigation happens
      onSuccess?.();
    } catch (error: any) {
      console.error('Failed to update team affiliations:', error);
      toast.error(error.response?.data?.error || 'Failed to update team affiliations');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Building2 className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Team Affiliation</h2>
              <p className="text-sm text-gray-600">Select teams associated with this publication</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isSubmitting}
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 flex-1 overflow-y-auto">
          {/* Publication Info */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Publication:</h3>
            <p className="text-gray-700">{publicationTitle}</p>
          </div>

          {/* Search Teams */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Select Teams</h3>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search teams by name, institution, or lab..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            {/* Team List */}
            {isLoadingTeams ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading teams...</p>
              </div>
            ) : filteredTeams.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Building2 className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                <p>No teams found</p>
              </div>
            ) : (
              <div className="border border-gray-300 rounded-lg max-h-64 overflow-y-auto">
                <div className="p-2 space-y-1">
                  {filteredTeams.map((team) => {
                    const isSelected = selectedTeamIds.includes(team.id);
                    return (
                      <label
                        key={team.id}
                        className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          isSelected 
                            ? 'bg-green-50 border-2 border-green-500' 
                            : 'hover:bg-gray-50 border-2 border-transparent'
                        }`}
                      >
                        <div className="flex-shrink-0">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            isSelected 
                              ? 'bg-green-600 border-green-600' 
                              : 'border-gray-300'
                          }`}>
                            {isSelected && <Check className="h-3 w-3 text-white" />}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900">{team.full_name}</div>
                          {(team.institution || team.lab_name) && (
                            <div className="text-sm text-gray-500 truncate">
                              {team.institution && team.lab_name 
                                ? `${team.institution} | ${team.lab_name}`
                                : team.institution || team.lab_name}
                            </div>
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleTeamToggle(team.id)}
                          className="sr-only"
                        />
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Selected Teams Summary */}
          {selectedTeamIds.length > 0 && (
            <div className="mb-6 p-4 bg-green-50 rounded-lg">
              <p className="text-sm font-medium text-gray-900 mb-2">
                Selected: {selectedTeamIds.length} team{selectedTeamIds.length !== 1 ? 's' : ''}
              </p>
              <div className="flex flex-wrap gap-2">
                {selectedTeamIds.map(teamId => {
                  const team = teams.find(t => t.id === teamId);
                  return team ? (
                    <span
                      key={teamId}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"
                    >
                      {team.full_name}
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeamAffiliationModal;
